import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Layout from "../../components/Layout";
import GoBackButton from "../../components/GoBackButton";
import "../../Order.css";
import { getCustomerOrderStats, getCustomerOrderHistory } from "../../api";
import moment from "moment-timezone";

const headerStyle = { whiteSpace: "nowrap", padding: "6px 8px", fontWeight: "bold" };
const cellStyle = { whiteSpace: "nowrap", padding: "6px 8px" };

function StatusBadge({ status }) {
  if (!status) return <>-</>;
  const label = status.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span
      className={`order-status-button ${status}`}
      style={{ padding: "3px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: 600, display: "inline-block" }}
    >
      {label}
    </span>
  );
}

function ProductsDialog({ open, onClose, products, orderId }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Order #{orderId} — Products</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={headerStyle}>Product</TableCell>
              <TableCell sx={headerStyle}>Qty</TableCell>
              <TableCell sx={headerStyle}>Variations</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products && products.map((product, idx) => (
              <TableRow key={idx} hover>
                <TableCell sx={cellStyle}>{product.name}</TableCell>
                <TableCell sx={cellStyle}>{product.quantity}</TableCell>
                <TableCell sx={{ padding: "6px 8px" }}>
                  {product.variations && product.variations.length > 0
                    ? product.variations.map((v) => `${v.key}: ${v.value}`).join(" | ")
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}

function OrderRow({ row }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const rawDate = row.date_created?.date || row.order_date || row.date;
  const date = rawDate
    ? moment(rawDate).tz("Australia/Melbourne").format("ll")
    : "-";

  const status = row.status || row.order_status;

  const productTitles = row.products && row.products.length > 0
    ? row.products.map((p) => p.name).join(", ")
    : "-";

  return (
    <>
      <TableRow hover>
        <TableCell sx={cellStyle}>{date}</TableCell>
        <TableCell
          sx={{ ...cellStyle, color: "#2271b1", fontWeight: "bold", cursor: "pointer" }}
          onClick={() => window.open(`/orders/1/details?id=${row.order_id}&tab=0`, "_blank")}
        >
          #{row.order_id}
        </TableCell>
        <TableCell sx={cellStyle}>
          <StatusBadge status={status} />
        </TableCell>
        <TableCell sx={{ padding: "6px 8px", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {productTitles}
        </TableCell>
        <TableCell sx={cellStyle}>${row.order_amount}</TableCell>
        <TableCell sx={{ padding: "6px 8px" }}>
          <IconButton size="small" onClick={() => setDialogOpen(true)}>
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      <ProductsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        products={row.products}
        orderId={row.order_id}
      />
    </>
  );
}

export default function ViewCustomer() {
  const { customerId } = useParams();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [orderHistory, setOrderHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await getCustomerOrderStats(customerId);
        setStats(data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };
    fetchStats();
  }, [customerId]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setHistoryLoading(true);
        const data = await getCustomerOrderHistory(customerId);
        setOrderHistory((data && data.orders) || []);
        setHistoryLoading(false);
      } catch (error) {
        console.log(error);
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [customerId]);

  return (
    <Layout>
      <Box
        bgcolor="#f0f0f1"
        minHeight={`calc(100vh - 67px)`}
        px={3}
        py={3}
        className={`secBox`}
      >
        <Box display="flex" alignItems="center" mb={3}>
          <GoBackButton />
          <Typography className="heading">Customer Details</Typography>
        </Box>

        {loading && <LinearProgress />}

        {stats && (
          <Box display="flex" gap={3} mt={3}>
            <Paper style={{ padding: "24px", minWidth: "200px", textAlign: "center", borderRadius: "8px", marginRight: "24px" }}>
              <Typography variant="h6" style={{ color: "#666", marginBottom: "8px" }}>Total Orders</Typography>
              <Typography variant="h3" style={{ fontWeight: "bold" }}>{stats.total_orders}</Typography>
            </Paper>
            <Paper style={{ padding: "24px", minWidth: "200px", textAlign: "center", borderRadius: "8px" }}>
              <Typography variant="h6" style={{ color: "#666", marginBottom: "8px" }}>Total Amount</Typography>
              <Typography variant="h3" style={{ fontWeight: "bold" }}>${stats.total_amount}</Typography>
            </Paper>
          </Box>
        )}

        <Box mt={4}>
          <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: "12px" }}>
            Order History
          </Typography>

          {historyLoading && <LinearProgress />}

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={headerStyle}>Date</TableCell>
                    <TableCell sx={headerStyle}>Order</TableCell>
                    <TableCell sx={headerStyle}>Status</TableCell>
                    <TableCell sx={headerStyle}>Products</TableCell>
                    <TableCell sx={headerStyle}>Amount</TableCell>
                    <TableCell sx={headerStyle}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderHistory.length > 0 ? (
                    orderHistory.map((row) => (
                      <OrderRow key={row.order_id} row={row} />
                    ))
                  ) : (
                    !historyLoading && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: "#888" }}>
                          No orders found
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>
    </Layout>
  );
}
